import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FileServiceService } from '../service/file-service.service';
import { FileCaptureService, StateOption } from 'app/service/file-capture.service';

@Component({
  standalone: false,
  selector: 'app-referance',
  templateUrl: './referance.component.html',
  styleUrls: ['./referance.component.css']
})
export class ReferanceComponent implements OnInit {
  @ViewChild('emailVerificationModal') emailVerificationModal!: TemplateRef<any>;

  contactForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.maxLength(60)]),
    lastName: new FormControl('', [Validators.maxLength(60)]),
    title: new FormControl('', [Validators.maxLength(60)]),
    organizationName: new FormControl('', [Validators.maxLength(120)]),
    contactEmail: new FormControl('', [Validators.required, Validators.maxLength(120), Validators.email, this.customEmailValidator]),
    contactPhone: new FormControl('', [Validators.maxLength(60)]),
    mailingAddress1: new FormControl('', [Validators.maxLength(120)]),
    mailingAddress2: new FormControl('', [Validators.maxLength(120)]),
    mailingCity: new FormControl('', [Validators.maxLength(80)]),
    mailingState: new FormControl(''),
    mailingZip: new FormControl('', [this.zipValidator])
  });

  verificationCode = '';
  verificationError = '';
  captchaValidated = false;
  isSendingCode = false;
  isVerifyingCode = false;
  states: StateOption[] = [];
  private modalRef: NgbModalRef | null = null;
  private readonly applicationCode = 'FileCaptureApp';

  constructor(
    private route: Router,
    public service: FileServiceService,
    public fileService: FileCaptureService,
    private modalService: NgbModal
  ) { }

  ngOnInit() {
    this.service.setPageVisited('reference');
    this.service.referenceFileDetails.referenceId = this.service.getCache('referenceId');
    this.applyConfiguredValidators();
    this.loadCachedValues();
    this.loadStates();

    this.contactForm.get('contactEmail')?.valueChanges.subscribe((newEmail) => {
      if (!newEmail || !this.service.isEmailAuthenticated(newEmail)) {
        this.service.setAuthenticatedEmail(null);
      }
    });
  }

  customEmailValidator(control: FormControl): { [key: string]: boolean } | null {
    const email = control.value;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return email && !emailRegex.test(email) ? { invalidEmailFormat: true } : null;
  }

  zipValidator(control: FormControl): { [key: string]: boolean } | null {
    const zip = control.value;
    if (!zip) {
      return null;
    }

    return /^\d{5}([-\s]?\d{4})?$/.test(zip) ? null : { invalidZip: true };
  }

  isRequired(controlName: string): boolean {
    return this.contactForm.get(controlName)?.hasValidator(Validators.required) ?? false;
  }

  shouldHighlightRequired(controlName: string): boolean {
    const control = this.contactForm.get(controlName);
    return !!control && this.isRequired(controlName) && control.invalid;
  }

  proceedToEmailVerification() {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.cacheContactValues();
    const email = this.contactForm.get('contactEmail')?.value;
    if (this.service.isEmailAuthenticated(email)) {
      this.route.navigate(['routing']);
      return;
    }

    this.verificationCode = '';
    this.verificationError = '';
    this.captchaValidated = false;
    this.isSendingCode = true;
    this.fileService.generateCode(email, this.service.referenceFileDetails.referenceId, this.applicationCode).subscribe({
      next: () => {
        this.isSendingCode = false;
        this.modalRef = this.modalService.open(this.emailVerificationModal, {
          centered: true,
          backdrop: 'static',
          keyboard: false,
          ariaLabelledBy: 'emailVerificationFormLabel'
        });
      },
      error: () => {
        this.isSendingCode = false;
        this.verificationError = 'Unable to send verification code. Please try again.';
      }
    });
  }

  validateCode() {
    if (!this.captchaValidated || !/^\d{6}$/.test(this.verificationCode)) {
      return;
    }

    this.isVerifyingCode = true;
    this.verificationError = '';
    const email = this.contactForm.get('contactEmail')?.value;
    this.fileService.validateCode({
      authCode: this.verificationCode,
      email,
      applicationCode: this.applicationCode
    }).subscribe({
      next: (isValid) => {
        this.isVerifyingCode = false;
        if (!isValid) {
          this.verificationError = 'Error! The value entered does not match the code sent to your email.';
          return;
        }

        this.service.setAuthenticatedEmail(email);
        this.modalRef?.close();
        this.route.navigate(['routing']);
      },
      error: () => {
        this.isVerifyingCode = false;
        this.verificationError = 'Unable to verify the code. Please try again.';
      }
    });
  }

  resendCode() {
    const email = this.contactForm.get('contactEmail')?.value;
    this.verificationCode = '';
    this.verificationError = '';
    this.isSendingCode = true;
    this.fileService.generateCode(email, this.service.referenceFileDetails.referenceId, this.applicationCode).subscribe({
      next: () => this.isSendingCode = false,
      error: () => {
        this.isSendingCode = false;
        this.verificationError = 'Unable to resend verification code. Please try again.';
      }
    });
  }

  cancelVerification() {
    this.modalRef?.close();
    this.modalRef = null;
    this.verificationCode = '';
    this.verificationError = '';
    this.captchaValidated = false;
  }

  onCaptchaResolved(response: string) {
    this.captchaValidated = !!response;
  }

  back() {
    const securityCode = window.localStorage.getItem('securityCode');
    this.route.navigate(['/' + securityCode]);
  }

  startOver() {
    const sessionId = window.sessionStorage.getItem(this.fileService.SESSION_KEY) || '';
    const payorKey = Number(window.sessionStorage.getItem(this.fileService.PAYOR_KEY) || 0);
    const referenceId = this.service.referenceFileDetails.referenceId || this.service.getCache('referenceId');

    this.fileService.releaseActiveReferenceLock(payorKey, referenceId, sessionId).subscribe();
    this.service.clearCache();
    this.fileService.fileArray = [];
    const securityCode = window.localStorage.getItem('securityCode');
    this.route.navigate(['/' + securityCode]);
  }

  private applyConfiguredValidators(): void {
    this.applyRequired('firstName', this.service.getConfigBoolean('FirstNameRequired', false));
    this.applyRequired('lastName', this.service.getConfigBoolean('LastNameRequired', false));
    this.applyRequired('title', this.service.getConfigBoolean('TitleRequired', false));
    this.applyRequired('organizationName', this.service.getConfigBoolean('OrganizationRequired', false));
    this.applyRequired('contactPhone', this.service.getConfigBoolean('PhoneRequired', false));
    this.applyRequired('mailingAddress1', this.service.getConfigBoolean('MailingAddress1Required', false));
    this.applyRequired('mailingCity', this.service.getConfigBoolean('MailingCityRequired', false));
    this.applyRequired('mailingState', this.service.getConfigBoolean('MailingStateRequired', false));
    this.applyRequired('mailingZip', this.service.getConfigBoolean('MailingZipRequired', false));
  }

  private applyRequired(controlName: string, required: boolean): void {
    const control = this.contactForm.get(controlName);
    if (!control) {
      return;
    }

    const validators = [];
    if (required) {
      validators.push(Validators.required);
    }

    if (controlName === 'mailingZip') {
      validators.push(this.zipValidator);
    }

    control.setValidators(validators.length ? validators : null);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private loadCachedValues(): void {
    Object.keys(this.contactForm.controls).forEach((key) => {
      const cachedValue = this.service.getCache(key);
      if (cachedValue !== null && cachedValue !== undefined) {
        this.contactForm.get(key)?.setValue(cachedValue);
      }
    });
  }

  private cacheContactValues(): void {
    const formValue = this.contactForm.value;
    this.service.referenceFileDetails.firstName = formValue.firstName;
    this.service.referenceFileDetails.lastName = formValue.lastName;
    this.service.referenceFileDetails.title = formValue.title;
    this.service.referenceFileDetails.organizationName = formValue.organizationName;
    this.service.referenceFileDetails.businessName = formValue.organizationName;
    this.service.referenceFileDetails.contactName = `${formValue.firstName || ''} ${formValue.lastName || ''}`.trim();
    this.service.referenceFileDetails.contactPhone = formValue.contactPhone;
    this.service.referenceFileDetails.contactEmail = formValue.contactEmail;
    this.service.referenceFileDetails.mailingAddress1 = formValue.mailingAddress1;
    this.service.referenceFileDetails.mailingAddress2 = formValue.mailingAddress2;
    this.service.referenceFileDetails.mailingCity = formValue.mailingCity;
    this.service.referenceFileDetails.mailingState = formValue.mailingState;
    this.service.referenceFileDetails.mailingZip = formValue.mailingZip;

    this.service.setCacheForitems([
      { id: 'firstName', value: formValue.firstName },
      { id: 'lastName', value: formValue.lastName },
      { id: 'title', value: formValue.title },
      { id: 'organizationName', value: formValue.organizationName },
      { id: 'businessName', value: formValue.organizationName },
      { id: 'contactName', value: this.service.referenceFileDetails.contactName },
      { id: 'contactPhone', value: formValue.contactPhone },
      { id: 'contactEmail', value: formValue.contactEmail },
      { id: 'mailingAddress1', value: formValue.mailingAddress1 },
      { id: 'mailingAddress2', value: formValue.mailingAddress2 },
      { id: 'mailingCity', value: formValue.mailingCity },
      { id: 'mailingState', value: formValue.mailingState },
      { id: 'mailingZip', value: formValue.mailingZip },
      { id: 'referenceId', value: this.service.getCache('referenceId') }
    ]);
  }

     private loadStates(): void {
    this.fileService.getStates().subscribe({
        next: (states) => this.states = states || [],
        error: () => this.states = []
    });
  }
}
